import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ArrowRight, Check, Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx'
import { cn, formatCurrency, isValidPhoneNumber } from '@/lib/utils.ts'
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE, getFlagEmoji } from '@/constants/countryCodes.ts'

// =============================================================================
// Types
// =============================================================================
interface CampaignPrice {
  price_id: string
  stripe_price_id: string
  commitment_months: number
  price_cents: number
  currency: string
}

interface CampaignProduct {
  product_id: string
  category: string
  tx_name_key: string
  tx_description_key: string
  prices: Array<CampaignPrice>
}

interface CampaignInfo {
  id: string
  slug: string
  language: 'de' | 'en'
  title_de: string
  title_en: string
  description_de?: string | null
  description_en?: string | null
}

interface CampaignData {
  campaign: CampaignInfo
  products: Array<CampaignProduct>
}

type CourseType = 'quran' | 'tajweed'

// =============================================================================
// Theme tokens (germanmethod.com design system)
// =============================================================================
const NAVY = '#150958'
const PURPLE = '#2d13be'
const TEXT_MUTED = '#757372'
const TEXT_DARK = '#1a1615'
const GOLD_CTA = '#e8dbb5'

// =============================================================================
// Helpers
// =============================================================================
function splitPrice(cents: number, currency: string, locale: string) {
  const whole = Math.floor(cents / 100)
  const fraction = (cents % 100).toString().padStart(2, '0')
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(0)
  const symbol = formatted.replace(/[\d\s.,\u00A0\u202F]/g, '').trim() || currency
  return { symbol, whole: whole.toLocaleString(locale), fraction }
}

const PRO_FEATURES = [
  'subscriptions.features.pro_feature_1',
  'subscriptions.features.pro_feature_2',
  'subscriptions.features.pro_feature_3',
] as const

const PREMIUM_EXTRA_FEATURES = [
  'subscriptions.features.premium_feature_2',
  'subscriptions.features.premium_feature_3',
  'subscriptions.features.premium_feature_4',
  'subscriptions.features.premium_feature_5',
] as const

// =============================================================================
// Main page
// =============================================================================
export function WebinarPricingPage() {
  const { slug } = useParams<{ slug: string }>()
  const { t, i18n } = useTranslation()
  const [campaign, setCampaign] = useState<CampaignData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
  const [courseType, setCourseType] = useState<CourseType>('quran')

  useEffect(() => {
    if (!slug) return
    loadCampaign()
  }, [slug])

  useEffect(() => {
    if (campaign?.campaign.language) {
      i18n.changeLanguage(campaign.campaign.language)
    }
  }, [campaign?.campaign.language])

  const isGerman = campaign?.campaign.language === 'de'
  const locale = isGerman ? 'de-DE' : 'en-US'

  const loadCampaign = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const res = await fetch(`/api/webinar/resolve?slug=${encodeURIComponent(slug!)}`, {
        credentials: 'include',
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        throw new Error(data.error?.message || t('webinar.pricing.notFound'))
      }
      setCampaign(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('webinar.pricing.loadError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleCheckout = async (campaignId: string, stripePriceId: string) => {
    setCheckoutLoading(stripePriceId)
    try {
      const res = await fetch('/api/webinar/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ campaign_id: campaignId, stripe_price_id: stripePriceId }),
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        throw new Error(data.error?.message || t('webinar.pricing.checkoutError'))
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('webinar.pricing.checkoutError'))
      setCheckoutLoading(null)
    }
  }

  if (isLoading) {
    return (
      <PageShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="flex items-center gap-3 font-[DM_Sans] text-sm tracking-wide text-[#757372]">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t('common.loading')}</span>
          </div>
        </div>
      </PageShell>
    )
  }

  if (error && !campaign) {
    return (
      <PageShell>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <h1
              className="font-[DM_Sans] text-3xl font-bold tracking-[-0.02em] mb-3"
              style={{
                background: 'linear-gradient(161.57deg, #0d113a 11.3%, #3a48e0 77.37%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('webinar.pricing.notFoundTitle')}
            </h1>
            <p className="font-[DM_Sans] text-[#757372]">{error}</p>
          </div>
        </div>
      </PageShell>
    )
  }

  if (!campaign) return null

  const proProduct = campaign.products.find((p) => p.category === 'pro') ?? null
  const premiumProduct = campaign.products.find((p) => p.category === 'premium') ?? null

  const title = isGerman ? campaign.campaign.title_de : campaign.campaign.title_en
  const description = isGerman ? campaign.campaign.description_de : campaign.campaign.description_en

  return (
    <PageShell>
      <div className="relative mx-auto max-w-[1240px] px-5 md:px-10 py-12 sm:py-16">
        {/* HEADER */}
        <header className="text-center fade-in flex flex-col items-center" style={{ animationDelay: '40ms' }}>
          <div className="font-[DM_Sans] uppercase tracking-[0.24em] text-[11px] sm:text-xs mb-5 font-semibold text-[#757372]">
            {isGerman ? 'Lerne den Quran lesen' : 'Learn to Read the Quran'}
          </div>
          <img
            src="/GM_Brand_2025_Logo - Horizontal.png"
            alt={title}
            className="h-14 sm:h-20 w-auto object-contain"
          />
          {description && (
            <p className="font-[DM_Sans] text-[15px] sm:text-base leading-relaxed max-w-lg mx-auto mt-5 text-[#757372]">
              {description}
            </p>
          )}
        </header>

        {/* COURSE TYPE SELECTOR */}
        <div
          className="mt-10 sm:mt-14 flex justify-center fade-in"
          style={{ animationDelay: '120ms' }}
        >
          <CourseTypeSelector courseType={courseType} setCourseType={setCourseType} t={t} />
        </div>

        {courseType === 'quran' && (
          <>
            {error && (
              <Alert variant="destructive" className="mt-8 max-w-md mx-auto font-[DM_Sans]">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* PRICING CARDS */}
            <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
              {proProduct && (
                <PricingCard
                  variant="pro"
                  t={t}
                  locale={locale}
                  product={proProduct}
                  campaignId={campaign.campaign.id}
                  checkoutLoading={checkoutLoading}
                  onCheckout={handleCheckout}
                  animationDelay="320ms"
                />
              )}
              {premiumProduct && (
                <PricingCard
                  variant="premium"
                  t={t}
                  locale={locale}
                  product={premiumProduct}
                  campaignId={campaign.campaign.id}
                  checkoutLoading={checkoutLoading}
                  onCheckout={handleCheckout}
                  animationDelay="440ms"
                />
              )}
            </div>
          </>
        )}

        {courseType === 'tajweed' && (
          <TajweedWaitlist t={t} animationDelay="180ms" />
        )}
      </div>
    </PageShell>
  )
}

// =============================================================================
// Page shell — ice/blue gradient background
// =============================================================================
function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative min-h-screen overflow-hidden font-[DM_Sans]"
      style={{
        background: 'linear-gradient(to bottom, #9cc1e7, #eef4ff 54.3%, rgba(238,244,255,0))',
        color: TEXT_DARK,
      }}
    >
      {/* Subtle bottom gradient wrap */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 inset-x-0 h-[50vh]"
        style={{
          background: 'linear-gradient(to bottom, rgba(244,248,255,0.1), #f4f8ff 55.19%, #9cc1e7)',
        }}
      />

      <div className="relative">{children}</div>

      <style>{`
        .fade-in {
          opacity: 0;
          transform: translateY(12px);
          animation: fadeInUp 700ms cubic-bezier(0.2, 0.65, 0.2, 1) forwards;
        }
        @keyframes fadeInUp {
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmerSlide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  )
}

// =============================================================================
// Pricing card — Pro (glass) or Premium (navy gradient) variants
// =============================================================================
interface PricingCardProps {
  variant: 'pro' | 'premium'
  product: CampaignProduct
  campaignId: string
  checkoutLoading: string | null
  onCheckout: (campaignId: string, stripePriceId: string) => void
  t: (key: string, opts?: Record<string, unknown>) => string
  locale: string
  animationDelay?: string
}

function PricingCard({
  variant,
  product,
  campaignId,
  checkoutLoading,
  onCheckout,
  t,
  locale,
  animationDelay,
}: PricingCardProps) {
  const isPremium = variant === 'premium'

  const selectedPrice = product.prices.find((p) => p.commitment_months === 6)
  if (!selectedPrice) return null

  const { symbol, whole, fraction } = splitPrice(
    selectedPrice.price_cents,
    selectedPrice.currency,
    locale,
  )

  const perMonthEquivalent = formatCurrency(
    Math.round(selectedPrice.price_cents / 6),
    selectedPrice.currency,
  )

  const subtitle = t('webinar.pricing.equivalentPerMonth', { price: perMonthEquivalent })

  const features = isPremium
    ? [...PRO_FEATURES, ...PREMIUM_EXTRA_FEATURES]
    : [...PRO_FEATURES]
  const greyedFeatures = isPremium ? [] : (PREMIUM_EXTRA_FEATURES as ReadonlyArray<string>)

  const theme = isPremium
    ? {
        bg: `linear-gradient(135deg, ${NAVY}, ${PURPLE})`,
        text: '#ffffff',
        textSoft: 'rgba(255,255,255,0.7)',
        accent: GOLD_CTA,
        checkBg: GOLD_CTA,
        checkColor: NAVY,
        ctaBg: GOLD_CTA,
        ctaText: NAVY,
      }
    : {
        bg: 'rgba(255,255,255,0.7)',
        text: TEXT_DARK,
        textSoft: TEXT_MUTED,
        accent: PURPLE,
        checkBg: `linear-gradient(to right, ${NAVY}, ${PURPLE})`,
        checkColor: '#ffffff',
        ctaBg: `linear-gradient(to right, ${NAVY}, ${PURPLE})`,
        ctaText: '#ffffff',
      }

  const isLoading = checkoutLoading === selectedPrice.stripe_price_id

  return (
    <div className="relative fade-in" style={{ animationDelay }}>
      <div
        className={cn(
          'relative h-full overflow-hidden rounded-3xl transition-transform duration-500 hover:-translate-y-1',
          !isPremium && 'backdrop-blur-md',
        )}
        style={{
          background: theme.bg,
          color: theme.text,
          border: isPremium ? 'none' : '1px solid rgba(255,255,255,0.4)',
          boxShadow: isPremium
            ? '0 20px 60px -15px rgba(21,9,88,0.4), 0 2px 16px 0 rgba(45,19,190,0.15)'
            : '0 2px 16px 0 rgba(180,200,240,0.15), inset 0 1px 2px 0 rgba(255,255,255,0.5)',
        }}
      >
        <div className="relative px-6 sm:px-8 pt-12 sm:pt-14 pb-10 sm:pb-12">
          {/* Plan name */}
          <div className="text-center">
            <div
              className="font-[DM_Sans] uppercase tracking-[0.2em] text-lg sm:text-xl font-bold mb-3"
              style={
                isPremium
                  ? { color: GOLD_CTA }
                  : {
                      background: `linear-gradient(122.48deg, ${NAVY} 13.13%, #624fce 77.66%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }
              }
            >
              {variant === 'pro' ? 'Pro' : 'Premium'}
            </div>
            {/* Tagline */}
            <div
              className="font-[DM_Sans] text-base sm:text-lg min-h-[2.6em] flex items-center justify-center px-2 font-medium"
              style={{ color: theme.textSoft }}
            >
              {isPremium
                ? t('subscription.premium.description')
                : t('subscription.pro.description')}
            </div>
          </div>

          {/* Price block */}
          <div className="mt-6 sm:mt-8 text-center">
            {/* One-time payment pill */}
            <div className="min-h-[1.75rem] flex items-center justify-center mb-3">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-[DM_Sans] uppercase tracking-[0.12em] text-[10px] sm:text-[11px] font-bold"
                style={{
                  background: isPremium
                    ? 'rgba(255,255,255,0.12)'
                    : `rgba(21,9,88,0.06)`,
                  color: theme.accent,
                  border: `1px solid ${isPremium ? 'rgba(232,219,181,0.3)' : 'rgba(45,19,190,0.2)'}`,
                }}
              >
                {t('webinar.pricing.oneTimePayment')}
              </span>
            </div>

            <div className="inline-flex items-start justify-center leading-none">
              <span
                className="font-[DM_Sans] font-bold mt-3 sm:mt-4 mr-1.5 text-2xl sm:text-3xl"
                style={{ color: theme.text }}
              >
                {symbol}
              </span>
              <span
                className="font-[DM_Sans] font-bold tracking-[-0.04em] text-[5rem] sm:text-[6.5rem]"
                style={{ color: theme.text, lineHeight: 0.9 }}
              >
                {whole}
              </span>
              <span
                className="font-[DM_Sans] font-bold mt-3 sm:mt-4 ml-0.5 text-xl sm:text-2xl"
                style={{ color: theme.text }}
              >
                ,{fraction}
              </span>
            </div>

            <div
              className="font-[DM_Sans] uppercase tracking-[0.18em] text-xs sm:text-sm mt-3 font-semibold"
              style={{ color: theme.textSoft }}
            >
              {t('webinar.pricing.perSixMonthsLong')}
            </div>

            <div
              className="font-[DM_Sans] text-sm sm:text-[15px] mt-5 px-4 font-medium"
              style={{ color: theme.textSoft }}
            >
              {subtitle}
            </div>
          </div>

          {/* Divider */}
          <div
            className="mt-9 mb-7 h-px"
            style={{
              background: isPremium
                ? 'linear-gradient(to right, transparent, rgba(255,255,255,0.15), transparent)'
                : 'linear-gradient(to right, transparent, rgba(21,9,88,0.1), transparent)',
            }}
          />

          {/* Features */}
          <ul className="space-y-3.5 mb-9 max-w-xs mx-auto">
            {features.map((key) => (
              <li key={key} className="flex items-start gap-3">
                <span
                  className="flex items-center justify-center w-5 h-5 flex-shrink-0 rounded-full mt-0.5"
                  style={{
                    background: isPremium ? theme.checkBg : theme.checkBg,
                    color: theme.checkColor,
                  }}
                  aria-hidden="true"
                >
                  <Check className="w-3 h-3" strokeWidth={3.5} />
                </span>
                <span
                  className="font-[DM_Sans] text-[15px] leading-snug font-medium"
                  style={{ color: theme.text }}
                >
                  {t(key)}
                </span>
              </li>
            ))}
            {greyedFeatures.map((key) => (
              <li key={key} className="flex items-start gap-3 opacity-40">
                <span
                  className="flex items-center justify-center w-5 h-5 flex-shrink-0 rounded-full mt-0.5 border"
                  style={{ borderColor: 'rgba(21,9,88,0.2)' }}
                  aria-hidden="true"
                />
                <span
                  className="font-[DM_Sans] text-[15px] leading-snug font-medium line-through"
                  style={{ color: theme.textSoft }}
                >
                  {t(key)}
                </span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <button
            onClick={() => onCheckout(campaignId, selectedPrice.stripe_price_id)}
            disabled={isLoading}
            className="group relative w-full overflow-hidden rounded-full py-[18px] px-6 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 hover:opacity-90"
            style={{
              background: theme.ctaBg,
              color: theme.ctaText,
            }}
          >
            {/* Shimmer sweep on hover */}
            <span
              className="pointer-events-none absolute inset-0 -translate-x-full group-hover:animate-[shimmerSlide_1.4s_ease-out]"
              style={{
                background: isPremium
                  ? 'linear-gradient(90deg, transparent, rgba(21,9,88,0.15), transparent)'
                  : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              }}
            />
            <span className="relative flex items-center justify-center gap-3 font-[DM_Sans] text-sm sm:text-[15px] font-semibold">
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  {variant === 'pro'
                    ? t('webinar.pricing.choosePro')
                    : t('webinar.pricing.choosePremium')}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Course type selector — pill toggle (Quran reading vs Tajweed)
// =============================================================================
function CourseTypeSelector({
  courseType,
  setCourseType,
  t,
}: {
  courseType: CourseType
  setCourseType: (c: CourseType) => void
  t: (key: string) => string
}) {
  const options: Array<{ value: CourseType; labelKey: string }> = [
    { value: 'quran', labelKey: 'webinar.pricing.courseType.quran' },
    { value: 'tajweed', labelKey: 'webinar.pricing.courseType.tajweed' },
  ]

  return (
    <div
      className="relative inline-flex items-center rounded-full p-1 font-[DM_Sans] text-sm"
      style={{
        backgroundColor: 'rgba(255,255,255,0.6)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.4)',
        boxShadow: '0 2px 16px 0 rgba(180,200,240,0.15)',
      }}
    >
      {options.map(({ value, labelKey }) => {
        const active = courseType === value
        return (
          <button
            key={value}
            onClick={() => setCourseType(value)}
            className={cn(
              'relative px-6 sm:px-8 py-3 rounded-full text-sm sm:text-[15px] font-semibold transition-all duration-300',
              active && 'text-white',
              !active && 'text-[#757372] hover:text-[#453f3d]',
            )}
            style={{
              background: active
                ? `linear-gradient(to right, ${NAVY}, ${PURPLE})`
                : 'transparent',
            }}
          >
            {t(labelKey)}
          </button>
        )
      })}
    </div>
  )
}

// =============================================================================
// Tajweed waitlist — coming soon card with signup form
// =============================================================================
function TajweedWaitlist({
  t,
  animationDelay,
}: {
  t: (key: string) => string
  animationDelay?: string
}) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [selectedCourse, setSelectedCourse] = useState<'tajweed' | 'understanding' | 'memorization'>('tajweed')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setPhoneError(null)

    const cleanedPhone = phoneNumber.replace(/\D/g, '')
    const fullPhone = `${countryCode}${cleanedPhone}`

    if (!cleanedPhone || !isValidPhoneNumber(fullPhone)) {
      setPhoneError(t('validation.phoneInvalid'))
      return
    }

    setIsSubmitting(true)
    try {
      const res = await fetch('/api/webinar/tajweed-waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email, phone: fullPhone, course: selectedCourse }),
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        throw new Error(data.error?.message || t('webinar.pricing.tajweed.errorGeneric'))
      }
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('webinar.pricing.tajweed.errorGeneric'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="mt-12 sm:mt-16 max-w-lg mx-auto fade-in"
      style={{ animationDelay }}
    >
      <div
        className="relative rounded-3xl backdrop-blur-md px-6 sm:px-10 py-10 sm:py-14"
        style={{
          background: 'rgba(255,255,255,0.7)',
          border: '1px solid rgba(255,255,255,0.4)',
          boxShadow:
            '0 2px 16px 0 rgba(180,200,240,0.15), inset 0 1px 2px 0 rgba(255,255,255,0.5)',
        }}
      >
        {submitted ? (
          <div className="text-center py-4">
            <div
              className="w-14 h-14 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ background: `linear-gradient(to right, ${NAVY}, ${PURPLE})` }}
            >
              <Check className="w-6 h-6 text-white" strokeWidth={3} />
            </div>
            <h2
              className="font-[DM_Sans] font-bold text-2xl mb-3 tracking-[-0.02em]"
              style={{
                background: 'linear-gradient(161.57deg, #0d113a 11.3%, #3a48e0 77.37%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('webinar.pricing.tajweed.successTitle')}
            </h2>
            <p className="font-[DM_Sans] text-[#757372] text-[15px]">
              {t('webinar.pricing.tajweed.successDescription')}
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="mb-5 flex flex-col items-center gap-2">
                <span
                  className="font-[DM_Sans] text-[11px] sm:text-[13px] font-semibold uppercase tracking-[0.18em]"
                  style={{ color: TEXT_MUTED }}
                >
                  Webinar Spezial
                </span>
              </div>
              <h2
                className="font-[DM_Sans] font-bold text-2xl sm:text-3xl mb-5 tracking-[-0.02em]"
                style={{
                  background: 'linear-gradient(161.57deg, #0d113a 11.3%, #3a48e0 77.37%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('webinar.pricing.tajweed.comingSoonTitle')}
              </h2>
              <Tabs
                value={selectedCourse}
                onValueChange={(v) => setSelectedCourse(v as typeof selectedCourse)}
                className="mb-5"
              >
                <TabsList className="flex flex-wrap w-full bg-white/60 border border-white/50">
                  <TabsTrigger value="tajweed" className="flex-1 text-xs sm:text-sm">
                    {t('webinar.pricing.tajweed.courseTabs.tajweed')}
                  </TabsTrigger>
                  <TabsTrigger value="understanding" className="flex-1 text-xs sm:text-sm">
                    {t('webinar.pricing.tajweed.courseTabs.understanding')}
                  </TabsTrigger>
                  <TabsTrigger value="memorization" className="flex-1 text-xs sm:text-sm">
                    {t('webinar.pricing.tajweed.courseTabs.memorization')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <p className="font-[DM_Sans] text-[#757372] text-[15px] leading-relaxed">
                {t(`webinar.pricing.tajweed.courseDescriptions.${selectedCourse}`)}
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-6 font-[DM_Sans]">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="tw-firstName" className="font-[DM_Sans] text-[#1a1615]">
                    {t('webinar.pricing.tajweed.firstNameLabel')}
                  </Label>
                  <Input
                    id="tw-firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('webinar.pricing.tajweed.firstNamePlaceholder')}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tw-lastName" className="font-[DM_Sans] text-[#1a1615]">
                    {t('webinar.pricing.tajweed.lastNameLabel')}
                  </Label>
                  <Input
                    id="tw-lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t('webinar.pricing.tajweed.lastNamePlaceholder')}
                    required
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tw-email" className="font-[DM_Sans] text-[#1a1615]">
                  {t('webinar.pricing.tajweed.emailLabel')}
                </Label>
                <Input
                  id="tw-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('webinar.pricing.tajweed.emailPlaceholder')}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tw-phone" className="font-[DM_Sans] text-[#1a1615]">
                  {t('webinar.pricing.tajweed.phoneLabel')}
                </Label>
                <div className="flex gap-2">
                  <div className="relative w-[140px] flex-shrink-0">
                    <select
                      id="tw-countryCode"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="w-full h-11 px-2 rounded-xl border border-border/50 bg-background font-[DM_Sans] text-sm focus:border-[#26247B] focus:ring-[#26247B]/20 focus:outline-none transition-colors appearance-none cursor-pointer"
                    >
                      {COUNTRY_CODES.map((country) => (
                        <option key={`${country.iso}-${country.dialCode}`} value={country.dialCode}>
                          {getFlagEmoji(country.iso)} {country.dialCode}
                        </option>
                      ))}
                    </select>
                    <svg
                      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                  <Input
                    id="tw-phone"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => { setPhoneNumber(e.target.value.replace(/\D/g, '')); setPhoneError(null) }}
                    placeholder="123456789"
                    required
                    className="flex-1 h-11"
                  />
                </div>
                {phoneError && (
                  <p className="font-[DM_Sans] text-sm text-red-500">{phoneError}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full overflow-hidden rounded-full py-[18px] px-6 mt-2 font-[DM_Sans] text-sm sm:text-[15px] font-semibold text-white transition-all duration-300 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: `linear-gradient(to right, ${NAVY}, ${PURPLE})` }}
              >
                <span
                  className="pointer-events-none absolute inset-0 -translate-x-full group-hover:animate-[shimmerSlide_1.4s_ease-out]"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                  }}
                />
                <span className="relative flex items-center justify-center gap-3">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('webinar.pricing.tajweed.submitting')}
                    </>
                  ) : (
                    <>
                      {t('webinar.pricing.tajweed.submitButton')}
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </span>
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
